import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface InlineEditableSectionProps {
  title: string;
  icon: React.ElementType;
  sectionKey: string;
  children: React.ReactNode;
  canEditAny: boolean;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving?: boolean;
  /** When true, the per-section Edit/Save/Cancel controls are hidden.
   *  Used by the global "edit everything at once" mode (e.g. the org employee popup). */
  hideControls?: boolean;
}

export function InlineEditableSection({
  title,
  icon: Icon,
  children,
  canEditAny,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  isSaving = false,
  hideControls = false,
}: InlineEditableSectionProps) {
  const { t } = useTranslation();

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
          <div className={cn("flex items-center gap-2", hideControls && "hidden")}>
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  disabled={isSaving}
                  className="h-7 px-2 text-xs"
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  {t("common.cancel")}
                </Button>
                <Button
                  size="sm"
                  onClick={onSave}
                  disabled={isSaving}
                  className="h-7 px-2 text-xs"
                >
                  {isSaving ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5 mr-1" />
                  )}
                  {isSaving ? t("common.saving") : t("common.save")}
                </Button>
              </>
            ) : canEditAny ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onEdit}
                className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              >
                <Pencil className="h-3.5 w-3.5 mr-1" />
                {t("common.edit")}
              </Button>
            ) : (
              <Badge variant="secondary" className="text-xs font-normal gap-1">
                <Lock className="h-3 w-3" />
                {t("profile.noEditRights")}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {children}
      </CardContent>
    </Card>
  );
}
